import { DatePicker as AntdDatePicker, Flex } from 'antd';
import type { RangePickerProps } from 'antd/es/date-picker';
import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import { ChevronDown, ChevronUp } from 'feather-icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useConstantsQuery } from 'modules/apiData/dataApiSlice';

import { ctvTwMerge } from 'services/twMerge/ctvTwMerge';

import { Button } from '../button';
import {
  useDateRangePresets,
  useDefaultDateRangePresets,
  UseDefaultDateRangePresetsOptions,
} from './dateRangePresets';

export type DateRange = {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  period: string;
};

export type DateRangeValue = [Dayjs | null, Dayjs | null] | null;

const AntdDateRangePicker = AntdDatePicker.RangePicker;
const CUSTOM = 'CUSTOM';
const MIN_DATE = dayjs('2024-01-01');

export const DateRangePicker = ({
  format = 'MMM D, YYYY',
  onChange = () => {},
  value = null,
  disabled,
  minDate,
  maxDate,
  className,
  placement,
  size,
  status,
  defaultValue,
  presetOptions,
  timeSlice,
  allowFutureDates = false,
}: {
  value?: DateRangeValue;
  defaultValue?: DateRangeValue;
  format?: string;
  disabled?: boolean;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  className?: string;
  placement?: RangePickerProps['placement'];
  size?: RangePickerProps['size'];
  status?: RangePickerProps['status'];
  onChange?: (range: DateRange) => void;
  presetOptions?: UseDefaultDateRangePresetsOptions;
  timeSlice?: string;
  allowFutureDates?: boolean;
}) => {
  const { presets: defaultPresets, isCampaignsLoaded } = useDefaultDateRangePresets();
  const presets = useDateRangePresets(minDate, maxDate, presetOptions);
  const { data: constants } = useConstantsQuery(null);

  const timeSlices = useMemo(() => constants?.TimeSlice || {}, [constants]);

  const getDefaultRange = () => {
    const fromPresets =
      presets.find((p) => p.label === timeSlices.CAMPAIGN_TO_DATE)?.value ||
      presets.find((p) => p.label === timeSlices.TODAY)?.value;

    return fromPresets || null;
  };

  const [initialValue, setInitialValue] = useState<DateRangeValue>(
    value ?? defaultValue ?? (getDefaultRange() as DateRangeValue),
  );

  const [_value, setValue] = useState<DateRangeValue>(initialValue);
  const [isOpen, setIsOpen] = useState(false);
  const [startDateError, setStartDateError] = useState(false);
  const [endDateError, setEndDateError] = useState(false);

  const startDate = useMemo(() => _value?.[0] || null, [_value]);
  const endDate = useMemo(() => _value?.[1] || null, [_value]);

  const findTimeSlice = useCallback(
    (val: DateRangeValue): string => {
      if (!val || !val[0] || !val[1]) return CUSTOM;

      const [start, end] = val;
      const format = 'YYYY-MM-DD';

      const match = defaultPresets.find(
        (p) =>
          p.value?.[0]?.format(format) === start.format(format) &&
          p.value?.[1]?.format(format) === end.format(format),
      );

      if (!match) return CUSTOM;

      return Object.keys(timeSlices).find((key) => timeSlices[key] === match.label) || CUSTOM;
    },
    [defaultPresets, timeSlices],
  );

  const isCustom = useCallback(
    (val: DateRangeValue) => findTimeSlice(val) === CUSTOM,
    [findTimeSlice],
  );

  useEffect(() => {
    if (value && value[0] && value[1]) {
      setValue(value);
      setInitialValue(value);
    }
  }, [value]);

  useEffect(() => {
    if (!value && isCampaignsLoaded && defaultValue && defaultValue[0] && defaultValue[1]) {
      setInitialValue(defaultValue);
      setValue(defaultValue);

      onChange({
        startDate: defaultValue[0],
        endDate: defaultValue[1],
        period: findTimeSlice(defaultValue),
      });
    }
  }, [value, defaultValue, onChange, findTimeSlice, isCampaignsLoaded]);

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleChange: RangePickerProps['onChange'] = (dates) => {
    if (!dates || !dates[0] || !dates[1]) {
      setValue(null);
      setInitialValue(null);

      onChange({ startDate: null, endDate: null, period: CUSTOM });

      return;
    }

    const adjusted: DateRangeValue = [dates[0], dates[1].endOf('day')];

    setValue(adjusted);
    setInitialValue(adjusted);
    handleClose();

    onChange({
      startDate: adjusted[0]!,
      endDate: adjusted[1]!,
      period: findTimeSlice(adjusted),
    });
  };

  const handleApply = () => {
    if (!startDate || !endDate) {
      setStartDateError(!startDate);
      setEndDateError(!endDate);

      return;
    }

    const adjusted: DateRangeValue = [startDate, endDate.endOf('day')];

    setValue(adjusted);
    setInitialValue(adjusted);

    onChange({
      startDate: adjusted[0]!,
      endDate: adjusted[1]!,
      period: findTimeSlice(adjusted),
    });

    handleClose();
  };

  const handleCancel = () => {
    setValue(initialValue);
    setStartDateError(false);
    setEndDateError(false);
    handleClose();
  };

  const handleDateBlur = (val: string, type: 'start' | 'end') => {
    if (!dayjs(val).isValid()) {
      type === 'start' ? setStartDateError(true) : setEndDateError(true);

      return;
    }

    const parsed = dayjs(val);
    const updated: DateRangeValue =
      type === 'start' ? [parsed, endDate] : [startDate, parsed.endOf('day')];

    setValue(updated);
    type === 'start' ? setStartDateError(false) : setEndDateError(false);
  };

  const shouldDisableApply =
    !_value?.[0] ||
    !_value?.[1] ||
    (_value[0]?.isSame(initialValue?.[0]) && _value[1]?.isSame(initialValue?.[1]));

  const footer = () => (
    <Flex justify="space-between" className="py-3">
      <Flex gap="small">
        <AntdDatePicker
          open={false}
          format={format}
          placeholder="Start date"
          value={startDate}
          onChange={(date) => setValue([date!, endDate])}
          status={startDateError ? 'error' : ''}
          onBlur={(e) => handleDateBlur((e.target as HTMLInputElement).value, 'start')}
        />
        -
        <AntdDatePicker
          open={false}
          format={format}
          placeholder="End date"
          value={endDate}
          onChange={(date) => setValue([startDate, date?.endOf('day') || null])}
          status={endDateError ? 'error' : ''}
          onBlur={(e) => handleDateBlur((e.target as HTMLInputElement).value, 'end')}
        />
      </Flex>

      <Flex gap="small">
        <Button onClick={handleCancel} variant="secondary">
          Cancel
        </Button>
        <Button onClick={handleApply} disabled={shouldDisableApply}>
          Apply
        </Button>
      </Flex>
    </Flex>
  );

  const label =
    timeSlice && !isOpen ? `[${timeSlices[timeSlice]}]` : `[${timeSlices[findTimeSlice(_value)]}]`;

  const showPresetLabel = !isOpen && (!isCustom(_value) || !!timeSlice);

  return (
    <AntdDateRangePicker
      disabled={disabled}
      onChange={handleChange}
      className={ctvTwMerge(
        classNames('ctv-picker', showPresetLabel && 'ctv-picker-preset'),
        className,
      )}
      rootClassName="ctv-picker-root"
      placement={placement}
      placeholder={['Start', 'End']}
      format={showPresetLabel ? label : format}
      separator={showPresetLabel ? undefined : '-'}
      suffixIcon={isOpen ? <ChevronUp /> : <ChevronDown />}
      allowClear={false}
      value={_value ?? undefined}
      defaultValue={defaultValue ?? undefined}
      minDate={minDate}
      maxDate={maxDate}
      presets={presets}
      open={isOpen}
      onOpenChange={(open) => (open ? handleOpen() : handleClose())}
      popupClassName="!p-0"
      renderExtraFooter={footer}
      size={size}
      status={status}
      disabledDate={(current) =>
        (!allowFutureDates && current && current > dayjs().endOf('day')) ||
        (current && current < MIN_DATE.startOf('day'))
      }
    />
  );
};
